import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Flex,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { MdContentCopy, MdSearch } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function JsonBlock({ value }) {
  if (value == null) return <Text color="gray.400" fontSize="sm">—</Text>;
  // Backend marks oversized JSON fields with this shape; render as a
  // truncation notice instead of pretty-printing megabytes.
  if (value && typeof value === "object" && value._truncated) {
    const sizeKb = value._size_bytes ? Math.round(value._size_bytes / 1024) : null;
    return (
      <Box
        bg="yellow.50"
        border="1px solid"
        borderColor="yellow.200"
        borderRadius="md"
        p={2}
      >
        <Text fontSize="xs" color="yellow.800" mb={1}>
          Truncated{sizeKb != null ? ` — original ~${sizeKb} KB` : ""}
        </Text>
        {value._preview && (
          <Box
            as="pre"
            fontSize="xs"
            fontFamily="mono"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            color="gray.700"
          >
            {value._preview}…
          </Box>
        )}
      </Box>
    );
  }
  let pretty;
  try {
    pretty = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  } catch {
    pretty = String(value);
  }
  if (!pretty || pretty === "{}" || pretty === "[]") {
    return <Text color="gray.400" fontSize="sm">—</Text>;
  }
  return (
    <Box
      as="pre"
      fontSize="xs"
      fontFamily="mono"
      bg="gray.50"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={2}
      maxH="320px"
      overflow="auto"
      whiteSpace="pre-wrap"
      wordBreak="break-word"
    >
      {pretty}
    </Box>
  );
}

function KV({ label, children }) {
  return (
    <Flex gap={2} fontSize="sm" align="baseline" wrap="wrap">
      <Text color="gray.500" minW="140px" flexShrink={0}>{label}</Text>
      <Box flex="1" minW={0}>
        {children == null || children === "" ? <Text color="gray.400">—</Text> : children}
      </Box>
    </Flex>
  );
}

function CopyableId({ value }) {
  if (!value) return <Text color="gray.400">—</Text>;
  return (
    <Flex align="center" gap={1}>
      <Code fontSize="xs">{value}</Code>
      <Tooltip label="Copy">
        <IconButton
          size="xs"
          variant="ghost"
          aria-label="Copy"
          icon={<MdContentCopy size={12} />}
          onClick={() => navigator.clipboard?.writeText(value)}
        />
      </Tooltip>
    </Flex>
  );
}

// ─── Message bubble ───────────────────────────────────────────────────────────

const SENDER_COLOR = {
  user: { bg: "blue.50", border: "blue.200", label: "blue" },
  assistant: { bg: "green.50", border: "green.200", label: "green" },
  bot: { bg: "green.50", border: "green.200", label: "green" },
  agent: { bg: "purple.50", border: "purple.200", label: "purple" },
  system: { bg: "gray.50", border: "gray.200", label: "gray" },
};

function MessageBubble({ msg, idx }) {
  const c = SENDER_COLOR[msg.sender_type] || SENDER_COLOR.system;
  return (
    <Box
      bg={c.bg}
      border="1px solid"
      borderColor={c.border}
      borderRadius="md"
      p={3}
    >
      <Flex justify="space-between" align="center" mb={1} gap={2} wrap="wrap">
        <Flex gap={2} align="center">
          <Text fontSize="xs" color="gray.500">#{idx + 1}</Text>
          <Badge colorScheme={c.label}>{msg.sender_type || "?"}</Badge>
          {msg.message_type && msg.message_type !== "text" && (
            <Badge variant="outline">{msg.message_type}</Badge>
          )}
          {msg.has_voice && <Badge colorScheme="orange">voice</Badge>}
        </Flex>
        <Text fontSize="xs" color="gray.500">{fmtDate(msg.created_at)}</Text>
      </Flex>
      <Text whiteSpace="pre-wrap" fontSize="sm">{msg.text || <Text as="span" color="gray.400">(empty)</Text>}</Text>
      {Array.isArray(msg.data_sources) && msg.data_sources.length > 0 && (
        <Box mt={2}>
          <Text fontSize="xs" color="gray.500" mb={1}>data_sources ({msg.data_sources.length})</Text>
          <JsonBlock value={msg.data_sources} />
        </Box>
      )}
      {typeof msg.char_count === "number" && msg.char_count > 0 && (
        <Text fontSize="xs" color="gray.400" mt={1}>{msg.char_count} chars</Text>
      )}
    </Box>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function InsightoAdminConversationViewer() {
  const baseUrl = getHostDomain();
  const navigate = useNavigate();
  const location = useLocation();

  // Optional ?id= query param to deep-link directly to a conversation
  const initialId = new URLSearchParams(location.search).get("id") || "";
  const [inputId, setInputId] = useState(initialId);
  const [status, setStatus] = useState(initialId ? "loading" : "idle"); // idle|loading|success|error
  const [errorMsg, setErrorMsg] = useState("");
  const [data, setData] = useState(null);

  async function fetchConversation(id) {
    const cid = (id || "").trim();
    if (!cid) return;
    setStatus("loading");
    setErrorMsg("");
    setData(null);
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/insighto-admin/conversation/${cid}`,
        "GET"
      );
      if (!res?.data) throw new Error(res?.message || "No data");
      setData(res.data);
      setStatus("success");
      // Sync URL so the view is shareable
      navigate(`/insighto-admin/conversation?id=${cid}`, { replace: true });
    } catch (e) {
      setStatus("error");
      setErrorMsg(e?.message || "Failed to load conversation");
    }
  }

  function handleLoad() {
    fetchConversation(inputId);
  }

  // Auto-load on mount when ?id=... is provided
  useEffect(() => {
    if (initialId) fetchConversation(initialId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const c = data?.conversation;
  const a = data?.assistant;
  const w = data?.widget;
  const ct = data?.contact;
  const msgs = data?.messages || [];
  const ds = data?.datasources || [];
  const ci = data?.captured_intents || [];
  const tf = data?.tool_invocations || [];
  const cs = data?.contact_sync_logs || [];
  const ul = data?.user_logs || [];

  // Totals from the backend; if total > rendered length, the section was capped.
  const msgsTotal = data?.messages_total ?? msgs.length;
  const ciTotal = data?.captured_intents_total ?? ci.length;
  const tfTotal = data?.tool_invocations_total ?? tf.length;
  const csTotal = data?.contact_sync_logs_total ?? cs.length;
  const ulTotal = data?.user_logs_total ?? ul.length;

  function TruncatedBanner({ shown, total }) {
    if (total <= shown) return null;
    return (
      <Alert status="warning" mb={3} borderRadius="md" fontSize="sm">
        <AlertIcon />
        Showing the most recent {shown.toLocaleString()} of {total.toLocaleString()} — older rows hidden.
      </Alert>
    );
  }

  return (
    <Box p={6}>
      <Heading size="lg" mb={4}>Conversation Viewer</Heading>

      <Flex gap={2} mb={6}>
        <Input
          placeholder="Paste conversation ID (UUID)"
          value={inputId}
          onChange={(e) => setInputId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleLoad();
            }
          }}
          fontFamily="mono"
          autoFocus
        />
        <Button
          type="button"
          colorScheme="blue"
          leftIcon={<MdSearch />}
          isLoading={status === "loading"}
          isDisabled={!inputId.trim()}
          onClick={handleLoad}
        >
          Load
        </Button>
      </Flex>

      {status === "error" && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {errorMsg}
        </Alert>
      )}

      {status === "loading" && (
        <Flex justify="center" py={8}><Spinner /></Flex>
      )}

      {status === "success" && data && (
        <VStack align="stretch" spacing={4}>
          {/* ── Overview ─────────────────────────────────────────── */}
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <Heading size="sm" mb={3}>Overview</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacingY={2} spacingX={6}>
              <KV label="Conversation ID"><CopyableId value={c?.id} /></KV>
              <KV label="Created">{fmtDate(c?.created_at)}</KV>
              <KV label="Updated">{fmtDate(c?.updated_at)}</KV>
              <KV label="Chat count">{c?.chat_count ?? 0}</KV>
              <KV label="Device">{c?.device_type || "—"}</KV>
              <KV label="Includes voice">
                <Badge colorScheme={c?.includes_voice ? "orange" : "gray"}>
                  {c?.includes_voice ? "yes" : "no"}
                </Badge>
              </KV>
              <KV label="Logged in">
                <Badge colorScheme={c?.is_logged_in ? "green" : "gray"}>
                  {c?.is_logged_in ? "yes" : "no"}
                </Badge>
              </KV>
              <KV label="Billed to">{c?.conversation_billed_to || "—"}</KV>
              <KV label="Assistant"><CopyableId value={c?.assistant_id} /></KV>
              <KV label="Widget"><CopyableId value={c?.widget_id} /></KV>
              <KV label="Contact"><CopyableId value={c?.contact_id} /></KV>
              <KV label="Unique device id">{c?.unique_device_id || "—"}</KV>
            </SimpleGrid>
            {c?.first_message && (
              <>
                <Divider my={3} />
                <KV label="First message">
                  <Text whiteSpace="pre-wrap" fontSize="sm">{c.first_message}</Text>
                </KV>
              </>
            )}
            {c?.summary && (
              <>
                <Divider my={3} />
                <KV label="Summary">
                  <Text whiteSpace="pre-wrap" fontSize="sm">{c.summary}</Text>
                </KV>
              </>
            )}
            {c?.attributes && Object.keys(c.attributes).length > 0 && (
              <>
                <Divider my={3} />
                <Text fontSize="sm" color="gray.500" mb={1}>attributes</Text>
                <JsonBlock value={c.attributes} />
              </>
            )}
            {c?.billing_details && Object.keys(c.billing_details).length > 0 && (
              <>
                <Divider my={3} />
                <Text fontSize="sm" color="gray.500" mb={1}>billing_details</Text>
                <JsonBlock value={c.billing_details} />
              </>
            )}
            {c?.provider_headers && Object.keys(c.provider_headers).length > 0 && (
              <>
                <Divider my={3} />
                <Text fontSize="sm" color="gray.500" mb={1}>provider_headers</Text>
                <JsonBlock value={c.provider_headers} />
              </>
            )}
          </Box>

          <Accordion allowMultiple defaultIndex={[0]}>
            {/* ── Thread ─────────────────────────────────────────── */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Thread ({msgsTotal})
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {isExpanded && (
                      <>
                        <TruncatedBanner shown={msgs.length} total={msgsTotal} />
                        {msgs.length === 0 ? (
                          <Text color="gray.400" fontSize="sm">No messages.</Text>
                        ) : (
                          <VStack align="stretch" spacing={2}>
                            {msgs.map((m, i) => <MessageBubble key={m.id} msg={m} idx={i} />)}
                          </VStack>
                        )}
                      </>
                    )}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>

            {/* ── Assistant ───────────────────────────────────────── */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Assistant {a ? `— ${a.name || "(unnamed)"}` : ""}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {isExpanded && (!a ? (
                      <Text color="gray.400" fontSize="sm">No assistant linked.</Text>
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacingY={2} spacingX={6}>
                        <KV label="ID"><CopyableId value={a.id} /></KV>
                        <KV label="Name">{a.name}</KV>
                        <KV label="Type">{a.assistant_type || "—"}</KV>
                        <KV label="LLM model">{a.llm_model || "—"}</KV>
                        <KV label="Voice">
                          <Badge colorScheme={a.voice ? "orange" : "gray"}>{a.voice ? "yes" : "no"}</Badge>
                        </KV>
                        <KV label="Custom voice">
                          <Badge colorScheme={a.custom_voice ? "orange" : "gray"}>{a.custom_voice ? "yes" : "no"}</Badge>
                        </KV>
                        <KV label="Uses tools">
                          <Badge colorScheme={a.use_tools ? "green" : "gray"}>{a.use_tools ? "yes" : "no"}</Badge>
                        </KV>
                        <KV label="Human agent">
                          <Badge colorScheme={a.has_human_agent ? "green" : "gray"}>{a.has_human_agent ? "yes" : "no"}</Badge>
                        </KV>
                        <KV label="Owner">{a.created_by_email || "—"}</KV>
                        <KV label="Created">{fmtDate(a.created_at)}</KV>
                        <KV label="Webhook"><CopyableId value={a.webhook_id} /></KV>
                        <KV label="Conv flow"><CopyableId value={a.conversation_flow_id} /></KV>
                        <Box gridColumn={{ md: "span 2" }}>
                          <KV label="Description">
                            <Text fontSize="sm">{a.description || "—"}</Text>
                          </KV>
                        </Box>
                        <Box gridColumn={{ md: "span 2" }}>
                          <Text fontSize="sm" color="gray.500" mb={1}>System prompt</Text>
                          {a.system_prompt
                            ? <Box as="pre" fontSize="xs" bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={2} maxH="320px" overflow="auto" whiteSpace="pre-wrap">{a.system_prompt}</Box>
                            : <Text color="gray.400" fontSize="sm">—</Text>}
                        </Box>
                        <Box gridColumn={{ md: "span 2" }}>
                          <Text fontSize="sm" color="gray.500" mb={1}>attributes</Text>
                          <JsonBlock value={a.attributes} />
                        </Box>
                      </SimpleGrid>
                    ))}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>

            {/* ── Widget ──────────────────────────────────────────── */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Widget {w ? `— ${w.display_name || w.name || w.widget_type || "(unnamed)"}` : ""}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {isExpanded && (!w ? (
                      <Text color="gray.400" fontSize="sm">No widget linked.</Text>
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacingY={2} spacingX={6}>
                        <KV label="ID"><CopyableId value={w.id} /></KV>
                        <KV label="Name">{w.name}</KV>
                        <KV label="Display name">{w.display_name || "—"}</KV>
                        <KV label="Type">{w.widget_type || "—"}</KV>
                        <KV label="Provider">{w.widget_provider || "—"}</KV>
                        <KV label="Owner">{w.created_by_email || "—"}</KV>
                        <KV label="Created">{fmtDate(w.created_at)}</KV>
                        <Box gridColumn={{ md: "span 2" }}>
                          <KV label="Description"><Text fontSize="sm">{w.description || "—"}</Text></KV>
                        </Box>
                        <Box gridColumn={{ md: "span 2" }}>
                          <KV label="Intro message">
                            <Text fontSize="sm" whiteSpace="pre-wrap">{w.intro_message || "—"}</Text>
                          </KV>
                        </Box>
                        <Box gridColumn={{ md: "span 2" }}>
                          <Text fontSize="sm" color="gray.500" mb={1}>attributes</Text>
                          <JsonBlock value={w.attributes} />
                        </Box>
                      </SimpleGrid>
                    ))}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>

            {/* ── Contact ─────────────────────────────────────────── */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Contact {ct ? `— ${[ct.first_name, ct.last_name].filter(Boolean).join(" ") || ct.email || ct.id}` : ""}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {isExpanded && (!ct ? (
                      <Text color="gray.400" fontSize="sm">No contact linked.</Text>
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacingY={2} spacingX={6}>
                        <KV label="ID"><CopyableId value={ct.id} /></KV>
                        <KV label="First name">{ct.first_name || "—"}</KV>
                        <KV label="Last name">{ct.last_name || "—"}</KV>
                        <KV label="Email">{ct.email || "—"}</KV>
                        <KV label="Last seen">{fmtDate(ct.last_seen)}</KV>
                        <KV label="Last sent">{fmtDate(ct.last_sent)}</KV>
                        <KV label="Created">{fmtDate(ct.created_at)}</KV>
                        <Box gridColumn={{ md: "span 2" }}>
                          <Text fontSize="sm" color="gray.500" mb={1}>channels</Text>
                          <JsonBlock value={ct.channels} />
                        </Box>
                        <Box gridColumn={{ md: "span 2" }}>
                          <Text fontSize="sm" color="gray.500" mb={1}>user_attributes</Text>
                          <JsonBlock value={ct.user_attributes} />
                        </Box>
                        <Box gridColumn={{ md: "span 2" }}>
                          <Text fontSize="sm" color="gray.500" mb={1}>custom_fields</Text>
                          <JsonBlock value={ct.custom_fields} />
                        </Box>
                      </SimpleGrid>
                    ))}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>

            {/* ── Data sources ─────────────────────────────────────── */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Data Sources ({ds.length})
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {isExpanded && (ds.length === 0 ? (
                      <Text color="gray.400" fontSize="sm">None.</Text>
                    ) : (
                      <Box overflowX="auto">
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>Name</Th>
                              <Th>Type</Th>
                              <Th>Status</Th>
                              <Th isNumeric>Words</Th>
                              <Th>Created</Th>
                              <Th>ID</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {ds.map((d) => (
                              <Tr key={d.id}>
                                <Td>{d.name || "—"}</Td>
                                <Td>{d.ds_type || "—"}</Td>
                                <Td>
                                  <Badge colorScheme={d.ds_status === "ready" ? "green" : "gray"}>
                                    {d.ds_status || "—"}
                                  </Badge>
                                </Td>
                                <Td isNumeric>{d.word_count ?? 0}</Td>
                                <Td whiteSpace="nowrap">{fmtDate(d.created_at)}</Td>
                                <Td><CopyableId value={d.id} /></Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    ))}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>

            {/* ── Captured intents ─────────────────────────────────── */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Captured Intents ({ciTotal})
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {isExpanded && (
                      <>
                        <TruncatedBanner shown={ci.length} total={ciTotal} />
                        {ci.length === 0 ? (
                          <Text color="gray.400" fontSize="sm">None.</Text>
                        ) : (
                          <Table size="sm">
                            <Thead>
                              <Tr>
                                <Th>Intent</Th>
                                <Th>Intent ID</Th>
                                <Th>Webhook ID</Th>
                                <Th>Captured at</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {ci.map((r) => (
                                <Tr key={r.id}>
                                  <Td>{r.intent_name || "—"}</Td>
                                  <Td><CopyableId value={r.intent_id} /></Td>
                                  <Td><CopyableId value={r.webhook_id} /></Td>
                                  <Td whiteSpace="nowrap">{fmtDate(r.created_at)}</Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        )}
                      </>
                    )}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>

            {/* ── Tool invocations ─────────────────────────────────── */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Tool Invocations ({tfTotal})
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {isExpanded && (
                      <>
                        <TruncatedBanner shown={tf.length} total={tfTotal} />
                        {tf.length === 0 ? (
                          <Text color="gray.400" fontSize="sm">None.</Text>
                        ) : (
                          <VStack align="stretch" spacing={2}>
                            {tf.map((r) => (
                              <Box key={r.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                                <Flex justify="space-between" wrap="wrap" gap={2} mb={2}>
                                  <Flex gap={2} align="center">
                                    <Badge colorScheme="purple">{r.tool_name || "?"}</Badge>
                                    <Text fontSize="sm" fontWeight="semibold">{r.function_name || "—"}</Text>
                                  </Flex>
                                  <Text fontSize="xs" color="gray.500">{fmtDate(r.created_at)}</Text>
                                </Flex>
                                <Text fontSize="xs" color="gray.500" mb={1}>arguments</Text>
                                <JsonBlock value={r.call_arguments} />
                                <Text fontSize="xs" color="gray.500" mt={2} mb={1}>response</Text>
                                <JsonBlock value={r.call_response} />
                              </Box>
                            ))}
                          </VStack>
                        )}
                      </>
                    )}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>

            {/* ── Contact sync logs ────────────────────────────────── */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Contact Sync Logs ({csTotal})
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {isExpanded && (
                      <>
                        <TruncatedBanner shown={cs.length} total={csTotal} />
                        {cs.length === 0 ? (
                          <Text color="gray.400" fontSize="sm">None.</Text>
                        ) : (
                          <VStack align="stretch" spacing={2}>
                            {cs.map((r) => (
                              <Box key={r.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                                <Flex justify="space-between" wrap="wrap" gap={2} mb={2}>
                                  <Flex gap={2} align="center">
                                    <Text fontSize="sm" fontWeight="semibold">Provider contact: {r.provider_contact_id || "—"}</Text>
                                    {r.provider_status_code != null && (
                                      <Badge colorScheme={r.provider_status_code >= 200 && r.provider_status_code < 300 ? "green" : "red"}>
                                        HTTP {r.provider_status_code}
                                      </Badge>
                                    )}
                                  </Flex>
                                  <Text fontSize="xs" color="gray.500">{fmtDate(r.created_at)}</Text>
                                </Flex>
                                <Text fontSize="xs" color="gray.500" mb={1}>extracted_data</Text>
                                <JsonBlock value={r.extracted_data} />
                                <Text fontSize="xs" color="gray.500" mt={2} mb={1}>fields_to_be_extracted</Text>
                                <JsonBlock value={r.fields_to_be_extracted} />
                                <Text fontSize="xs" color="gray.500" mt={2} mb={1}>provider_response</Text>
                                <JsonBlock value={r.provider_response} />
                              </Box>
                            ))}
                          </VStack>
                        )}
                      </>
                    )}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>

            {/* ── User logs ────────────────────────────────────────── */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      User Logs ({ulTotal})
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {isExpanded && (
                      <>
                        <TruncatedBanner shown={ul.length} total={ulTotal} />
                        {ul.length === 0 ? (
                          <Text color="gray.400" fontSize="sm">None.</Text>
                        ) : (
                          <Table size="sm">
                            <Thead>
                              <Tr>
                                <Th>Type</Th>
                                <Th>Message</Th>
                                <Th>Attributes</Th>
                                <Th>At</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {ul.map((r) => (
                                <Tr key={r.id}>
                                  <Td><Badge>{r.log_type || "—"}</Badge></Td>
                                  <Td><Text fontSize="sm" whiteSpace="pre-wrap">{r.message || "—"}</Text></Td>
                                  <Td><JsonBlock value={r.attributes} /></Td>
                                  <Td whiteSpace="nowrap">{fmtDate(r.created_at)}</Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        )}
                      </>
                    )}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          </Accordion>
        </VStack>
      )}
    </Box>
  );
}
